import * as React from "react";
import {useCallback, useState} from "react";

import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";

import SuccessToast from "../success-toast.tsx";

import SubscriptionSwitch from "./subscription-switch.tsx";
import UPDATE_SUBSCRIPTION_BY_TOKEN_MUTATION from "./update-subscription-by-token-mutation.ts";

/**
 * Hold the checked state of an email subscription and sync it with the server.
 *
 * This component auto sends changes to the server.
 */

interface Props {
    checked: boolean;
    subscriptionKey?: string;
    label: string;
    showError: () => void;
    clearError: () => void;
    onChange?: (checked: boolean) => unknown;
    inputId: string;
}

export const EmailSubscriptionSwitch: React.FC<Props> = ({
    checked: initialChecked,
    subscriptionKey,
    label,
    showError,
    clearError,
    onChange,
    inputId,
}) => {
    const [checked, setChecked] = useState(initialChecked);
    const [isLoading, setIsLoading] = useState(false);

    const [updateSubscription, result] = useMutation(
        UPDATE_SUBSCRIPTION_BY_TOKEN_MUTATION,
    );

    const handleChange = useCallback(
        async (newChecked: boolean) => {
            clearError();

            if (onChange) {
                onChange(newChecked);
            }

            setChecked(newChecked);
            setIsLoading(true);

            try {
                const result = await updateSubscription({
                    token: subscriptionKey ?? "",
                    subscribe: newChecked,
                });

                if (result.status === "error") {
                    throw new KAError(
                        "Failed to update subscription",
                        Errors.Internal,
                        {cause: result.error},
                    );
                }

                if (
                    result.status === "success" &&
                    result.data?.updateSubscriptionByToken?.error
                ) {
                    throw new KAError(
                        result.data.updateSubscriptionByToken.error.code,
                        Errors.Internal,
                    );
                }
            } catch (error) {
                setChecked(!newChecked);
                showError();
            } finally {
                setIsLoading(false);
            }
        },
        [clearError, onChange, updateSubscription, subscriptionKey, showError],
    );

    return (
        <>
            <SubscriptionSwitch
                checked={checked}
                label={label}
                isLoading={isLoading}
                onChange={handleChange}
                inputId={inputId}
            />
            {result?.status === "success" && (
                <SuccessToast onClose={() => undefined} />
            )}
        </>
    );
};

export default EmailSubscriptionSwitch;
